import React from "react"
import Keymap from "../plugins/keymap"
import { renderToStaticMarkup } from "react-dom/server"
import { uniq } from "lodash"
import RenderContext from "../render-context"
import { InheritedThemeProvider } from "@convertkit/editor"
import { createStore } from "core/store"
import { getNodesFromFolder } from "bundler/editor-get-nodes-from-folder"

const { useStore: useHoverNode, set: setHoverNode } = createStore({})

let nodes = getNodesFromFolder()

for (let key in nodes) {
  const node = nodes[key]

  if (node.nodes) {
    for (let subkey in node.nodes) {
      const subnode = node.nodes[subkey]
      nodes[subkey] = subnode
    }
  }
}

const isType = type => editor => editor?.value?.startBlock?.type === type

const Plugin = () => {
  let schema = {}
  let keymaps = []
  let commands = {}

  for (let key in nodes) {
    const node = nodes[key]

    if (node.schema) {
      schema[key] = node.schema
    }

    if (node.keymap) {
      keymaps.push(Keymap(node.keymap, { if: isType(key) }))
    }

    if (node.commands) {
      commands = { ...commands, ...node.commands }
    }
  }

  return [
    {
      commands,
      schema: {
        blocks: schema
      },
      renderNode: (props, editor, next) => {
        const { attributes, children, node } = props
        if (!node.object === "block") return next()
        if (!nodes[node.type]) return next()

        // In Slate `data` is an ImmutableJS object. Eventually, we'd like to remove
        // ImmutableJS as a dependency. To help with that, we can convert it to a normal
        // JS object for consumption by any nodes here to isolate ImmutableJS specific
        // code to a smaller number of locations.
        const data = node.data.toJS()

        const { component: Component } = nodes[node.type]

        if (!Component) return next()

        return (
          <Component
            attributes={attributes}
            node={node}
            data={data}
            editor={editor}
          >
            {children}
          </Component>
        )
      }
    },
    ...keymaps
  ]
}

const stylesForNode = node => {
  let styles = []

  const config = nodes[node.type]

  if (config?.styles) {
    const Styles = config.styles

    styles.push(
      renderToStaticMarkup(<Styles />)
        .replace(/<outlook-only>/g, "<!--[if mso]>")
        .replace(/<\/outlook-only>/g, "<![endif]-->")
    )
  }

  if (node.nodes) {
    node.nodes.forEach(subnode => {
      styles = styles.concat(stylesForNode(subnode))
    })
  }

  return styles
}

export const styles = doc => uniq(stylesForNode(doc)).join(" ")

export const serializerRule = (options = {}) => ({
  serialize(node, children) {
    if (node.object !== "block") return
    if (!nodes[node.type]) return

    const config = nodes[node.type]

    const Component =
      options.target === "email"
        ? config.email || config.component
        : config.component

    if (!Component) return

    return (
      <RenderContext.Provider value={{ target: options.target, mode: "live" }}>
        <InheritedThemeProvider theme={options.theme}>
          <Component
            attributes={{}}
            node={node}
            data={node.data.toJS()}
            serializingFind={options.find}
          >
            {children}
          </Component>
        </InheritedThemeProvider>
      </RenderContext.Provider>
    )
  }
})

export { useHoverNode, setHoverNode }
export default Plugin
